import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Check, Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { resolveMedia } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Reveal } from "@/components/motion/Reveal";

export default function Library() {
  useSEO({
    title: "Library — Guide gratuite di interior design",
    description: "Guide e risorse gratuite di Giovanna Matino Interior: ristrutturazione, illuminazione, materiali e progettazione. Scarica il tuo Book in PDF.",
  });
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", privacy: false, marketing: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/library/books").then((r) => setBooks(r.data)).catch(() => setBooks([])).finally(() => setLoading(false));
  }, []);

  const openBook = (b) => {
    setSel(b); setDone(null);
    setForm({ name: "", email: "", privacy: false, marketing: false, website: "" });
  };

  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.privacy;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/library/books/${sel.slug}/download`, {
        name: form.name, email: form.email,
        privacy_consent: form.privacy, marketing_consent: form.marketing, website: form.website,
      });
      setDone(data);
      window.open(data.download_url, "_blank", "noopener");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la registrazione. Riprova.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-library">
      {/* Hero editoriale */}
      <section className="bg-ink text-bone">
        <div className="container-x pt-12 pb-16 md:pt-16 md:pb-20 max-w-3xl">
          <span className="eyebrow !text-gold">Interior Design Library</span>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6">
            Guide gratuite, <em className="text-gold font-light">scritte da chi progetta.</em>
          </h1>
          <p className="mt-8 text-bone/70 text-lg font-light leading-relaxed max-w-xl">
            Ristrutturazione, illuminazione, materiali, budget: le guide dello Studio per affrontare
            il tuo progetto con consapevolezza. Scaricabili gratuitamente in PDF.
          </p>
        </div>
      </section>

      {/* Griglia Book */}
      <section className="container-x py-16 md:py-20">
        {loading && <p className="text-taupe">Caricamento…</p>}
        {!loading && books.length === 0 && (
          <div className="text-center py-16 max-w-lg mx-auto" data-testid="library-empty">
            <BookOpen size={28} className="mx-auto text-gold" />
            <h2 className="font-serif text-3xl mt-4">La Library sta arrivando.</h2>
            <p className="text-taupe mt-3 text-sm leading-relaxed">Lo Studio sta preparando le prime guide. Nel frattempo puoi calcolare la stima gratuita del tuo progetto.</p>
            <Link to="/stima" className="btn-ink mt-8 inline-flex">Calcola la tua stima gratuita</Link>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.06}>
              <button onClick={() => openBook(b)} className="group text-left w-full" data-testid={`book-${b.slug}`}>
                <div className="relative overflow-hidden bg-white border border-hair">
                  {b.cover ? (
                    <img src={resolveMedia(b.cover)} alt={b.title} loading="lazy"
                      className="w-full h-[300px] md:h-[340px] object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-[300px] md:h-[340px] bg-ink text-bone flex items-center justify-center">
                      <BookOpen size={34} className="text-gold" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-bone/95 text-ink text-[10px] tracking-[0.14em] uppercase px-2 py-1">{b.category}</span>
                </div>
                <div className="mt-5">
                  <h2 className="font-serif text-2xl leading-tight group-hover:text-gold transition-colors">{b.title}</h2>
                  {b.description && <p className="text-sm text-taupe mt-2 leading-relaxed line-clamp-3">{b.description}</p>}
                  <span className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-ink group-hover:text-gold transition-colors">
                    Scarica gratuitamente <ArrowUpRight size={14} />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Cross-link stima */}
        <div className="mt-20 border border-hair bg-white p-8 md:p-12 text-center" data-testid="library-cta-stima">
          <span className="eyebrow text-gold">Il passo successivo</span>
          <h2 className="font-serif text-3xl md:text-4xl mt-4">Dal libro al tuo progetto.</h2>
          <p className="text-taupe mt-4 text-sm max-w-xl mx-auto leading-relaxed">
            Le guide ti orientano; la stima ti dà i numeri. Calcola gratuitamente il costo preliminare
            del tuo progetto, calcolato sulle tariffe reali dello Studio.
          </p>
          <Link to="/stima" className="btn-ink mt-8 inline-flex" data-testid="library-cta-stima-btn">Calcola la tua stima gratuita</Link>
        </div>
      </section>

      {/* Modal download */}
      {sel && (
        <div className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSel(null)}>
          <div className="relative max-w-lg w-full bg-bone p-8 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="book-modal">
            <button onClick={() => setSel(null)} className="absolute top-4 right-4 p-2 min-w-[44px] min-h-[44px] hover:bg-gold hover:text-bone transition-colors" data-testid="book-close" aria-label="Chiudi">✕</button>
            {done ? (
              <div className="text-center py-4" data-testid="book-success">
                <div className="mx-auto w-12 h-12 border border-gold text-gold flex items-center justify-center"><Check size={22} /></div>
                <h3 className="font-serif text-3xl mt-5">La guida è tua.</h3>
                <p className="text-taupe mt-3 text-sm leading-relaxed">
                  Il download è partito in una nuova scheda e ti abbiamo inviato la guida anche via email.
                </p>
                <a href={done.download_url} target="_blank" rel="noopener" className="btn-ink mt-6 inline-flex items-center gap-2" data-testid="book-download-again">
                  <Download size={15} /> Apri di nuovo il PDF
                </a>
                <p className="text-[11px] text-taupe mt-5">Vuoi andare oltre la guida? <Link to="/stima" className="underline hover:text-ink">Calcola la stima gratuita del tuo progetto</Link>.</p>
              </div>
            ) : (
              <>
                <div className="eyebrow text-gold">{sel.category}</div>
                <h3 className="font-serif text-3xl mt-2">{sel.title}</h3>
                <p className="text-sm text-taupe mt-3 leading-relaxed">Lascia nome ed email: la guida si apre subito e te la inviamo anche via email. Gratuita, per sempre.</p>
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="book-form">
                  <div>
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome *</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="book-name" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Email *</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" data-testid="book-email" />
                  </div>
                  {/* honeypot */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="book-website" />
                  </div>
                  <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                    <input type="checkbox" checked={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.checked })} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="book-privacy" />
                    <span>Ho letto l'<Link to="/privacy" target="_blank" className="underline hover:text-ink">informativa privacy</Link> e acconsento al trattamento dei dati per ricevere la guida. *</span>
                  </label>
                  <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                    <input type="checkbox" checked={form.marketing} onChange={(e) => setForm({ ...form, marketing: e.target.checked })} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="book-marketing" />
                    <span>Desidero ricevere guide, consigli, ispirazioni e aggiornamenti da Giovanna Matino Interior. (facoltativo — la guida la ricevi comunque)</span>
                  </label>
                  <button type="submit" disabled={busy || !valid} className="btn-ink w-full min-h-[48px] disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="book-submit">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                    {busy ? "Preparazione…" : "Scarica la guida gratuita"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
